/**
 * 8D Module API - Claims
 *
 * Database operations for 8D claims.
 * Uses mod_8d schema for module isolation.
 */

import { addMonths } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';

// =============================================================================
// Types
// =============================================================================

export interface Claim {
  id: string;
  tenant_id: string;
  factory_id: string;
  supplier_id: string;
  claim_number: string;
  title: string;
  status: string;
  magic_token: string;
  magic_token_expires_at: string;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface ClaimWithRelations extends Claim {
  supplier?: {
    name: string;
    code: string;
    contact_email: string | null;
    // Note: mod_8d.suppliers doesn't have contact_name or contact_phone
    // These fields exist in public.suppliers but not in mod_8d.suppliers
  } | null;
  creator?: {
    full_name: string | null;
    email: string | null;
  } | null;
  factory?: {
    name: string;
  } | null;
  steps?: Array<{
    step_number: number;
    status: string;
    completed_at: string | null;
  }>;
}

export interface MagicTokenUpdate {
  magic_token: string;
  magic_token_expires_at: string;
}

export interface CreateClaimInput {
  tenant_id: string;
  factory_id: string;
  supplier_id: string;
  claim_number: string;
  title: string;
  created_by: string;
}

// =============================================================================
// Queries
// =============================================================================

/**
 * Fetches claims for a factory with supplier info.
 * Note: Creator info is fetched separately due to cross-schema limitation
 * (profiles table is in 'core' schema, claims are in 'mod_8d' schema).
 */
export async function fetchClaimsByFactory(factoryId: string): Promise<ClaimWithRelations[]> {
  const { data, error } = await supabase
    .schema('mod_8d')
    .from('claims')
    .select(`
      *,
      supplier:suppliers(name, code, contact_email)
    `)
    .eq('factory_id', factoryId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data as unknown as ClaimWithRelations[];
}

/**
 * Fetches a single claim by ID with supplier details.
 * Note: Creator info is fetched separately due to cross-schema limitation
 * (profiles table is in 'core' schema, claims are in 'mod_8d' schema).
 */
export async function fetchClaimById(claimId: string): Promise<ClaimWithRelations | null> {
  const { data, error } = await supabase
    .schema('mod_8d')
    .from('claims')
    .select(`
      *,
      supplier:suppliers(name, code, contact_email)
    `)
    .eq('id', claimId)
    .single();

  if (error) throw error;
  return data as unknown as ClaimWithRelations;
}

/**
 * Fetches a claim by magic token (for supplier portal access).
 * Note: Factory info is fetched separately due to cross-schema limitation
 * (factories table is in 'core' schema, claims are in 'mod_8d' schema).
 */
export async function fetchClaimByMagicToken(token: string): Promise<ClaimWithRelations | null> {
  const { data, error } = await supabase
    .schema('mod_8d')
    .from('claims')
    .select(`
      *,
      supplier:suppliers(name, code, contact_email)
    `)
    .eq('magic_token', token)
    .gt('magic_token_expires_at', new Date().toISOString())
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null; // Not found
    throw error;
  }

  // Fetch factory info separately due to cross-schema limitation
  const claim = data as unknown as ClaimWithRelations;
  if (claim.factory_id) {
    const factory = await fetchFactory(claim.factory_id);
    claim.factory = factory;
  }

  return claim;
}

/**
 * Fetches claims with steps for dashboard view.
 */
export async function fetchClaimsWithSteps(factoryId: string): Promise<ClaimWithRelations[]> {
  const { data, error } = await supabase
    .schema('mod_8d')
    .from('claims')
    .select(`
      *,
      supplier:suppliers(name),
      steps(step_number, status, completed_at)
    `)
    .eq('factory_id', factoryId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data as unknown as ClaimWithRelations[];
}

/**
 * Generates a unique claim number for a factory.
 */
export async function generateClaimNumber(tenantId: string): Promise<string> {
  const { data, error } = await supabase
    .schema('mod_8d')
    .rpc('generate_claim_number', { p_tenant_id: tenantId });

  if (error) throw error;
  return data;
}

/**
 * Creates a new 8D claim.
 */
export async function createClaim(input: CreateClaimInput): Promise<Claim> {
  const { data, error } = await supabase
    .schema('mod_8d')
    .from('claims')
    .insert({
      tenant_id: input.tenant_id,
      factory_id: input.factory_id,
      supplier_id: input.supplier_id,
      claim_number: input.claim_number,
      title: input.title,
      created_by: input.created_by,
    })
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Updates claim status.
 */
export async function updateClaimStatus(claimId: string, status: string): Promise<void> {
  const { error } = await supabase
    .schema('mod_8d')
    .from('claims')
    .update({ status })
    .eq('id', claimId);

  if (error) throw error;
}

/**
 * Regenerates supplier portal magic token and expiry.
 */
export async function regenerateClaimMagicToken(claimId: string): Promise<MagicTokenUpdate> {
  const magic_token = crypto.randomUUID();
  const magic_token_expires_at = addMonths(new Date(), 6).toISOString();

  const { data, error } = await supabase
    .schema('mod_8d')
    .from('claims')
    .update({ magic_token, magic_token_expires_at })
    .eq('id', claimId)
    .select('magic_token, magic_token_expires_at')
    .single();

  if (error) throw error;
  return data as MagicTokenUpdate;
}

/**
 * Gets the claim status for validation.
 */
export async function getClaimStatus(claimId: string): Promise<string | null> {
  const { data, error } = await supabase
    .schema('mod_8d')
    .from('claims')
    .select('status')
    .eq('id', claimId)
    .single();

  if (error) throw error;
  return data?.status ?? null;
}

/**
 * Fetches creator info for a claim.
 * This is a separate query due to cross-schema limitation.
 */
export async function fetchClaimCreator(userId: string): Promise<{
  full_name: string | null;
  email: string | null;
} | null> {
  const { data, error } = await supabase
    .schema('core')
    .from('profiles')
    .select('full_name, email')
    .eq('id', userId)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null; // Not found
    throw error;
  }
  return data;
}

/**
 * Fetches factory info for a claim.
 * This is a separate query due to cross-schema limitation
 * (factories table is in 'core' schema, claims are in 'mod_8d' schema).
 */
export async function fetchFactory(factoryId: string): Promise<{
  name: string;
} | null> {
  const { data, error } = await supabase
    .schema('core')
    .from('factories')
    .select('name')
    .eq('id', factoryId)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null; // Not found
    throw error;
  }
  return data;
}

/**
 * Fetches claim with supplier info for rejection emails.
 */
export async function fetchClaimForRejection(claimId: string): Promise<{
  claim_number: string;
  title: string;
  magic_token: string;
  supplier: {
    name: string;
    contact_email: string | null;
  } | null;
} | null> {
  const { data, error } = await supabase
    .schema('mod_8d')
    .from('claims')
    .select(`
      claim_number,
      title,
      magic_token,
      supplier:suppliers(name, contact_email)
    `)
    .eq('id', claimId)
    .single();

  if (error) throw error;
  return data as unknown as {
    claim_number: string;
    title: string;
    magic_token: string;
    supplier: {
      name: string;
      contact_email: string | null;
    } | null;
  };
}
